import { useState, useRef, useEffect } from 'react'
import { FaRegCalendarCheck } from 'react-icons/fa'
import { FaArrowRightLong } from 'react-icons/fa6'
import Calendar from './Calendar'
import styles from './DatePicker.module.css'

const DatePicker = ({ onDateChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [selectingStart, setSelectingStart] = useState(true)
    const datePickerRef = useRef(null)

    const formatDate = (date) => {
        if (!date) return ''
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        })
    }

    const handleInputClick = (isStartInput) => {
        setIsOpen(true)
        setSelectingStart(isStartInput)
    }

    const handleDateSelect = (date) => {
        if (selectingStart) {
            setStartDate(date)
            setEndDate(null)
            setSelectingStart(false)
        } else {
            if (date < startDate) {
                setStartDate(date)
                setEndDate(null)
                setSelectingStart(false)
            } else {
                setEndDate(date)
                setIsOpen(false)
                setSelectingStart(true)

                if (onDateChange) {
                    // Format both dates to `YYYY-MM-DDTHH:mm:ss`
                    const formatToCustomISO = (d) => {
                        if (!d) return null
                        const pad = (n) => String(n).padStart(2, '0')
                        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
                            d.getHours()
                        )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
                    }

                    onDateChange({
                        startDate: formatToCustomISO(startDate),
                        endDate: formatToCustomISO(date),
                    })
                }
            }
        }
    }

    const handleClickOutside = (event) => {
        if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
            setIsOpen(false)
            setSelectingStart(true)
        }
    }

    const handleClear = () => {
        setStartDate('')
        setEndDate('')
        setSelectingStart(true)
        if (onDateChange) {
            onDateChange({ startDate: '', endDate: '' })
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <div className={styles.datePickerContainer} ref={datePickerRef}>
            <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                    <input
                        type="text"
                        placeholder="Start date"
                        value={formatDate(startDate)}
                        onClick={() => handleInputClick(true)}
                        className={`${styles.dateInput} ${selectingStart && isOpen ? styles.active : ''}`}
                        readOnly
                    />
                </div>
                <span className={styles.separator}>
                    <FaArrowRightLong />
                </span>
                <div className={styles.inputWrapper}>
                    <input
                        type="text"
                        placeholder="End date"
                        value={formatDate(endDate)}
                        onClick={() => handleInputClick(false)}
                        className={`${styles.dateInput} ${!selectingStart && isOpen ? styles.active : ''}`}
                        readOnly
                    />
                </div>
                <div className={styles.calendarIcon} onClick={() => setIsOpen(!isOpen)}>
                    <FaRegCalendarCheck />
                </div>
                {(startDate || endDate) && (
                    <button className={styles.clearButton} onClick={handleClear}>
                        ✕
                    </button>
                )}
            </div>

            {isOpen && (
                <div className={styles.calendarPopup}>
                    <Calendar
                        startDate={startDate}
                        endDate={endDate}
                        selectingStart={selectingStart}
                        onDateSelect={handleDateSelect}
                        onClose={() => setIsOpen(false)}
                    />
                </div>
            )}
        </div>
    )
}

export default DatePicker
